// Exposes the collab Worker's `GET /collab/availability` decision to the UI,
// so the three entry points (top-right button, main-menu item,
// welcome-screen item) can disable themselves *before* a doomed reservation
// attempt spends the Worker's own request quota. See `docs/superpowers/specs/
// 2026-09-05-realtime-collab-cloudflare-do-plan.html`, section 3, "按鈕怎麼禁用".
//
// Caching policy (this query itself costs Cloudflare request quota, so it
// must not run on every render or every page load in a loop):
//  - Result is kept in one module-level cache, shared by every mounted
//    instance of this hook (the three entry points can all call it without
//    tripling the fetch count).
//  - A mount only refetches if there is no cached result yet, or the cached
//    one has gone stale. "Stale" is driven by the Worker's own
//    `Cache-Control: private, max-age=N` (see `AVAILABILITY_CACHE_CONTROL`
//    in `workers/collab/src/worker.ts`), not a value invented here. When the
//    header is missing or unparseable, `DEFAULT_STALE_AFTER_MS` is used
//    instead of treating the response as fresh forever.
//  - A background interval (`BACKGROUND_REFRESH_INTERVAL_MS`) re-checks
//    staleness for a long-lived tab, but only actually refetches when the
//    cached entry's own max-age has elapsed — the interval is "how often to
//    check whether it is time yet", not "how often to hit the network".
//  - `refresh()` is exposed for the one case that should bypass the cache on
//    purpose: right before the user opens the collaboration dialog, so a
//    tab that has been idle for hours does not act on an hours-old "allowed".
//
// Fails safe: a network error or a malformed response is recorded as
// `allowed: false` (never left as stale/optimistic data), so a Worker outage
// disables collaboration instead of silently allowing it.

import { useCallback, useEffect, useSyncExternalStore } from "react";

import { DRAWOLA_FEATURES } from "../drawola-features";

import {
  fetchCollabAvailability,
  getCollabWorkerBaseUrl,
  type CollabAvailabilityReason,
  type CollabLimits,
} from "./collabSession";

export type { CollabAvailabilityReason, CollabLimits };

export interface CollabAvailability {
  allowed: boolean;
  /** `"unknown"` covers both "still loading" and "the check itself failed". */
  reason: CollabAvailabilityReason | "unknown";
  remainingSessions: number | null;
  resetAt: string | null;
  limits: CollabLimits | null;
  isLoading: boolean;
}

// Fallback cache lifetime when the Worker response carries no parseable
// `Cache-Control: max-age=N`. Generous on purpose: this query spends the
// Worker's own request quota (see module docstring above).
const DEFAULT_STALE_AFTER_MS = 5 * 60 * 1000;

// How often a long-lived tab re-checks whether the cache has gone stale.
// Deliberately far above the Worker's own 15s max-age: that header stops one
// browser from double-fetching within the same click, it is not an
// invitation to poll every 15 seconds.
const BACKGROUND_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const FAIL_CLOSED: Omit<CollabAvailability, "isLoading"> = {
  allowed: false,
  reason: "unknown",
  remainingSessions: null,
  resetAt: null,
  limits: null,
};

const INITIAL_STATE: CollabAvailability = { ...FAIL_CLOSED, isLoading: true };

let cached: { state: CollabAvailability; staleAtMs: number } | null = null;
let inFlightRequest: Promise<void> | null = null;
const subscribers = new Set<() => void>();

const notifySubscribers = (): void => {
  for (const listener of subscribers) {
    listener();
  }
};

const getSnapshot = (): CollabAvailability => cached?.state ?? INITIAL_STATE;

const subscribe = (listener: () => void): (() => void) => {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
};

/**
 * Runs the actual fetch, updating the shared cache and notifying every
 * subscriber. Never throws: `fetchCollabAvailability` already fails closed
 * on a network error or a malformed body (returns `null`), which this maps
 * to the same fail-closed `CollabAvailability` state.
 */
const runFetch = async (): Promise<void> => {
  // `fetchCollabAvailability` already fails closed (catches its own network
  // errors and returns `null`); this second try/catch is defense in depth so
  // a misbehaving dependency can never leave an unhandled rejection here or
  // skip clearing `inFlightRequest`.
  let result: Awaited<ReturnType<typeof fetchCollabAvailability>> = null;
  try {
    result = await fetchCollabAvailability(getCollabWorkerBaseUrl());
  } catch (error) {
    console.error(
      "Collaboration availability check failed unexpectedly",
      error,
    );
  }
  const staleAfterMs = result?.maxAgeMs ?? DEFAULT_STALE_AFTER_MS;
  cached = {
    state: result
      ? {
          allowed: result.allowed,
          reason: result.reason,
          remainingSessions: result.remainingSessions,
          resetAt: result.resetAt,
          limits: result.limits,
          isLoading: false,
        }
      : { ...FAIL_CLOSED, isLoading: false },
    staleAtMs: Date.now() + staleAfterMs,
  };
  notifySubscribers();
};

/** Refetches only when there is no cached result yet, or it has gone stale. */
const ensureFresh = (): void => {
  if (cached && Date.now() < cached.staleAtMs) {
    return;
  }
  if (inFlightRequest) {
    return;
  }
  inFlightRequest = runFetch().finally(() => {
    inFlightRequest = null;
  });
};

/** Forces the next `ensureFresh()` to hit the network regardless of staleness. */
const invalidate = (): void => {
  cached = null;
};

/**
 * Test-only escape hatch: clears the shared module cache so each test starts
 * from a clean slate. Never call this from application code.
 */
export const resetCollabAvailabilityCacheForTests = (): void => {
  cached = null;
  inFlightRequest = null;
};

/**
 * Reads the collab Worker's availability decision for the current caller.
 * Returns the fail-closed initial state (loading, not allowed) until the
 * first fetch resolves, and never fetches at all while both collaboration
 * flags are off (the default), since nothing would use the result anyway.
 */
export const useCollabAvailability = (): CollabAvailability & {
  refresh: () => void;
} => {
  const state = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => INITIAL_STATE,
  );

  useEffect(() => {
    if (
      !DRAWOLA_FEATURES.SHOW_LIVE_COLLAB &&
      !DRAWOLA_FEATURES.SHOW_SHARE_BUTTON
    ) {
      return undefined;
    }
    ensureFresh();
    const interval = window.setInterval(
      ensureFresh,
      BACKGROUND_REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
  }, []);

  const refresh = useCallback(() => {
    invalidate();
    ensureFresh();
  }, []);

  return { ...state, refresh };
};
